import { useEffect, useState } from "react";
import { api, type Branch, type PullRequest, type GiteaRepo } from "../api/client";

const DEFAULT_REPO = "Creatix/coagent-console";

export function Branches() {
  const [repos, setRepos] = useState<GiteaRepo[]>([]);
  const [repo, setRepo] = useState<string>(DEFAULT_REPO);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [pulls, setPulls] = useState<PullRequest[]>([]);
  const [state, setState] = useState<"open" | "closed" | "all">("open");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .repos()
      .then((d) => {
        setRepos(d.repos);
        if (d.repos.length > 0 && !d.repos.find((r) => r.full_name === repo)) {
          setRepo(d.repos[0]!.full_name);
        }
      })
      .catch((e) => setErr(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!repo) return;
    setLoading(true);
    Promise.all([api.branches(repo), api.pulls(repo, state)])
      .then(([b, p]) => {
        setBranches(b.branches);
        setPulls(p.pulls);
        setErr(null);
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [repo, state]);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Ветки и PR</h1>
          <p className="text-sm text-panel-muted">
            Gitea ({repos.length} репо). Настрой <code>GITEA_TOKEN</code> в .env если список пустой.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-panel-muted">Репозиторий:</label>
          <select
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            className="rounded border border-panel-border bg-panel-bg px-2 py-1 text-sm"
          >
            {repos.length === 0 && <option value={DEFAULT_REPO}>{DEFAULT_REPO}</option>}
            {repos.map((r) => (
              <option key={r.full_name} value={r.full_name}>
                {r.full_name}
              </option>
            ))}
          </select>
          <div className="flex gap-1">
            {(["open", "closed", "all"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setState(s)}
                className={
                  "rounded border px-2 py-1 text-xs " +
                  (state === s
                    ? "border-panel-accent text-panel-accent"
                    : "border-panel-border text-panel-muted hover:text-panel-fg")
                }
              >
                {s === "open" ? "открытые" : s === "closed" ? "закрытые" : "все"}
              </button>
            ))}
          </div>
        </div>
      </header>

      {err && <div className="card pill-err border-panel-err">Ошибка: {err}</div>}
      {loading && <div className="text-xs text-panel-muted">Загрузка…</div>}

      <section>
        <h2 className="mb-2 text-sm font-medium text-panel-muted">PR ({pulls.length})</h2>
        <div className="space-y-2">
          {pulls.map((pr) => (
            <div key={pr.number} className="card flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-panel-muted">#{pr.number}</span>
                  <span className="font-medium">{pr.title}</span>
                  <span className={`pill text-[10px] ${pr.state === "open" ? "pill-ok" : ""}`}>
                    {pr.state}
                  </span>
                </div>
                <div className="mt-1 text-xs text-panel-muted">
                  {pr.user} · {pr.head_branch} → {pr.base_branch} ·{" "}
                  {new Date(pr.updated_at).toLocaleDateString()}
                </div>
              </div>
              <a href={pr.url} target="_blank" rel="noopener" className="btn text-xs">
                Открыть
              </a>
            </div>
          ))}
          {pulls.length === 0 && !loading && (
            <div className="text-xs text-panel-muted">PR с фильтром «{state}» нет.</div>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium text-panel-muted">Ветки ({branches.length})</h2>
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-panel-muted">
              <tr>
                <th className="pb-2 pr-2">Имя</th>
                <th className="pb-2 pr-2">SHA</th>
                <th className="pb-2 pr-2">Коммит</th>
                <th className="pb-2">Дата</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.name} className="border-t border-panel-border">
                  <td className="py-1 pr-2 font-medium">
                    {b.name}
                    {b.protected && <span className="pill ml-1 text-[10px]">🔒</span>}
                  </td>
                  <td className="py-1 pr-2 font-mono text-xs text-panel-muted">
                    {b.commit_sha?.slice(0, 8)}
                  </td>
                  <td className="py-1 pr-2 text-xs text-panel-muted">{b.commit_message ?? ""}</td>
                  <td className="py-1 text-xs text-panel-muted">
                    {b.commit_date ? new Date(b.commit_date).toLocaleDateString() : ""}
                  </td>
                </tr>
              ))}
              {branches.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="py-2 text-center text-xs text-panel-muted">
                    Веток нет или нет доступа к репо.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
